import type { SiteListFilters } from '../types';
import type { InventoryODataRequest } from './types';

// Inventory dataset endpoint caps the `top` parameter at 250.
const MAX_DATASET_PAGE_SIZE = 250;
const DEFAULT_DATASET_PAGE_SIZE = 250;
const DEFAULT_RESOURCE_PAGE_SIZE = 100;

const cleanODataRequest = (request: InventoryODataRequest) =>
  Object.fromEntries(
    Object.entries(request).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  ) as InventoryODataRequest;

const escapeODataString = (value: string) => value.replace(/'/g, "''");

const stringLiteral = (value: string) => `'${escapeODataString(value)}'`;

const eq = (field: string, value: string | number) =>
  typeof value === 'number'
    ? `${field} eq ${value}`
    : `${field} eq ${stringLiteral(value)}`;

const ne = (field: string, value: string | number) =>
  typeof value === 'number'
    ? `${field} ne ${value}`
    : `${field} ne ${stringLiteral(value)}`;

const contains = (field: string, value: string) =>
  `contains(${field}, ${stringLiteral(value)})`;

const or = (clauses: string[]) =>
  clauses.length > 1 ? `(${clauses.join(' or ')})` : clauses[0];

const and = (clauses: Array<string | undefined>) =>
  clauses.filter(Boolean).join(' and ');

const numericValue = (value: string | null | undefined) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildSiteFlagshipEnhancedClause = (
  flagship: boolean | undefined,
  enhanced: boolean | undefined,
) => {
  if (flagship && enhanced) {
    return or([eq('networkTier', 'flagship'), eq('networkTier', 'enhanced')]);
  }

  if (flagship === true) {
    return eq('networkTier', 'flagship');
  }

  if (enhanced === true) {
    return eq('networkTier', 'enhanced');
  }

  if (flagship === false && enhanced === false) {
    return and([ne('networkTier', 'flagship'), ne('networkTier', 'enhanced')]);
  }

  if (flagship === false) {
    return ne('networkTier', 'flagship');
  }

  if (enhanced === false) {
    return ne('networkTier', 'enhanced');
  }

  return undefined;
};

export const buildInventorySitesOData = (
  filters: SiteListFilters = {},
): InventoryODataRequest => {
  const pageSize = Math.min(
    MAX_DATASET_PAGE_SIZE,
    Math.max(1, filters.pageSize ?? DEFAULT_DATASET_PAGE_SIZE),
  );
  const skip = numericValue(filters.cursor) ?? 0;
  const siteIdNumber = numericValue(filters.siteId);
  const clauses = [
    filters.search
      ? or([
          contains('name', filters.search),
          contains('facility', filters.search),
          contains('physicalAddress', filters.search),
        ])
      : undefined,
    filters.siteId
      ? siteIdNumber === null
        ? eq('name', filters.siteId)
        : or([eq('id', siteIdNumber), eq('name', filters.siteId)])
      : undefined,
    filters.siteName ? contains('name', filters.siteName) : undefined,
    filters.region ? eq('regionName', filters.region) : undefined,
    filters.siteType ? eq('siteType', filters.siteType) : undefined,
    filters.networkTier ? eq('networkTier', filters.networkTier) : undefined,
    filters.primaryDivision
      ? contains('primaryDivision', filters.primaryDivision)
      : undefined,
    filters.businessUnit
      ? contains('businessUnits', filters.businessUnit)
      : undefined,
    filters.datacenter === true ? eq('siteType', 'datacenter') : undefined,
    filters.datacenter === false ? ne('siteType', 'datacenter') : undefined,
    buildSiteFlagshipEnhancedClause(filters.flagship, filters.enhanced),
  ];

  return cleanODataRequest({
    count: true,
    filter: and(clauses),
    orderby: 'name asc',
    select:
      'id,name,slug,status,regionId,regionName,group,facility,timeZone,description,physicalAddress,shippingAddress,latitude,longitude,comments,siteType,networkTier,primaryDivision,businessUnits,circuitCount,deviceCount,prefixCount,rackCount,virtualMachineCount,vlanCount',
    skip,
    top: pageSize,
  });
};

export const buildInventoryResourcesOData = ({
  siteId,
  skip = 0,
  tag,
  top = DEFAULT_RESOURCE_PAGE_SIZE,
}: {
  siteId?: string | null;
  skip?: number;
  tag?: string | null;
  top?: number;
}): InventoryODataRequest => {
  const normalizedSiteId = siteId?.trim();
  const normalizedTag = tag?.trim();
  const tagClauses = [
    normalizedSiteId
      ? or([
          eq('tagWkApplicationBitId', normalizedSiteId),
          contains('tagWkAdditionalBitIds', normalizedSiteId),
          contains('tagWkApplicationName', normalizedSiteId),
          contains('tagWkResourceName', normalizedSiteId),
        ])
      : undefined,
    normalizedTag
      ? or([
          eq('tagWkApplicationBitId', normalizedTag),
          contains('tagWkAdditionalBitIds', normalizedTag),
          contains('tagWkApplicationName', normalizedTag),
          contains('tagWkResourceName', normalizedTag),
        ])
      : undefined,
  ].filter(Boolean) as string[];

  return cleanODataRequest({
    count: false,
    filter: or(tagClauses),
    select:
      'id,name,type,subscriptionId,region,vendor,tagWkDivisionCode,tagWkBuCode,tagWkApplicationName,tagWkApplicationBitId,tagWkEnvironmentName,tagWkEnvironmentType,tagWkDataClassification,tagWkResourceName,tagWkPatchClass,tagWkRequestor,tagWkBusinessOwner,tagWkTechnicalOwner,tagWkAppSupportGroup,tagWkInfraSupportGroup,tagWkAdditionalBitIds,createdAt,updatedAt,properties',
    skip,
    top,
  });
};

export const buildNetboxSiteResourcesOData = ({
  select = 'id,name,siteId,details',
  siteNumericId,
  skip = 0,
  top = DEFAULT_RESOURCE_PAGE_SIZE,
}: {
  select?: string;
  siteNumericId: number | string;
  skip?: number;
  top?: number;
}): InventoryODataRequest =>
  cleanODataRequest({
    count: false,
    filter: eq('siteId', Number(siteNumericId)),
    select,
    skip,
    top,
  });
